import type { HeroQuest, Quest } from '@/game/types';
import type { HeroResponse } from '@/network/hero.api';
import { apiGet, apiPost, apiDelete } from '@/network/api-client';

interface HeroQuestRaw {
  id: number;
  heroId: number;
  questId: number;
  status: string;
  progress: number;
  quest?: {
    id: number;
    npcId: number;
    questKey?: string;
    title: string;
    description: string;
    type: string;
    targetCount: number;
    targetEnemyType?: string;
    targetTownId?: number;
    targetTownName?: string;
    dropChance: number;
    minLevel: number;
    maxLevel: number;
    rewardXp: number;
    rewardGold: number;
    rewardPotions: number;
  };
  title?: string;
  description?: string;
  type?: string;
  targetCount?: number;
  rewardXp?: number;
  rewardGold?: number;
  rewardPotions?: number;
  npcName?: string;
  townName?: string;
}

function flattenHeroQuest(raw: HeroQuestRaw): HeroQuest {
  const q = raw.quest;
  return {
    id: raw.id,
    questId: raw.questId,
    questKey: q?.questKey,
    questNpcId: q?.npcId,
    title: raw.title ?? q?.title ?? '',
    description: raw.description ?? q?.description ?? '',
    type: raw.type ?? q?.type ?? '',
    targetCount: raw.targetCount ?? q?.targetCount ?? 0,
    targetTownName: raw.quest?.targetTownName ?? q?.targetTownName,
    targetTownId: q?.targetTownId != null ? Number(q.targetTownId) : undefined,
    progress: raw.progress,
    status: (raw.status as HeroQuest['status']) ?? 'accepted',
    rewardXp: raw.rewardXp ?? q?.rewardXp ?? 0,
    rewardGold: raw.rewardGold ?? q?.rewardGold ?? 0,
    rewardPotions: raw.rewardPotions ?? q?.rewardPotions ?? 0,
    npcName: raw.npcName ?? '',
    townName: raw.townName ?? '',
  };
}

export async function getHeroQuests(telegramId?: number): Promise<HeroQuest[]> {
  const query = telegramId != null ? `?telegramId=${telegramId}` : '';
  const raw = await apiGet<HeroQuestRaw[]>(`/hero/quests${query}`);
  return raw.map(flattenHeroQuest);
}

export async function acceptQuest(questId: number, telegramId?: number): Promise<HeroQuest> {
  const query = telegramId != null ? `?telegramId=${telegramId}` : '';
  return apiPost<HeroQuest>(`/hero/quests/${questId}/accept${query}`);
}

export async function claimQuest(heroQuestId: number, telegramId?: number): Promise<HeroResponse> {
  const query = telegramId != null ? `?telegramId=${telegramId}` : '';
  return apiPost<HeroResponse>(`/hero/quests/${heroQuestId}/claim${query}`);
}

export async function abandonQuest(heroQuestId: number, telegramId?: number): Promise<void> {
  const query = telegramId != null ? `?telegramId=${telegramId}` : '';
  return apiDelete<void>(`/hero/quests/${heroQuestId}${query}`);
}

export async function getNPCQuests(npcId: number, telegramId?: number): Promise<Quest[]> {
  const query = telegramId != null ? `?telegramId=${telegramId}` : '';
  const data = await apiGet<Quest[] | null>(`/npcs/${npcId}/quests${query}`);
  return Array.isArray(data) ? data : [];
}
